// **Library Imports**
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "mdb-vue-ui-kit/css/mdb.min.css";
import { createApp } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import Vue3Toastify, { toast } from "vue3-toastify";

// **Core Framework Imports**
import App from "./App.vue";
import router from "./router";
import store from "./store";

// **Plugins and Services**
import ApiService from "./services/api.service";
import { MaintenanceService } from "./services/maintanace.service";
import { detectCountryAndLoadConfig } from "./utils/tenantUtils";
import { startVersionPolling } from "./utils/versionCheck";
import { applyPublicSeoTags } from "./utils/publicSeo";
import { createI18n } from "vue-i18n";
import TokenService from "@/services/storage.service";

// **Validation Libraries**
import { defineRule, configure } from "vee-validate";
import AllRules from "@vee-validate/rules";
import { localize, setLocale } from "@vee-validate/i18n";
import ar from "@vee-validate/i18n/dist/locale/ar.json";
import az from "@vee-validate/i18n/dist/locale/az.json";
import bg from "@vee-validate/i18n/dist/locale/bg.json";
import bn from "@vee-validate/i18n/dist/locale/bn.json";
import ca from "@vee-validate/i18n/dist/locale/ca.json";
import cs from "@vee-validate/i18n/dist/locale/cs.json";
import cy from "@vee-validate/i18n/dist/locale/cy.json";
import de from "@vee-validate/i18n/dist/locale/de.json";
import en from "@vee-validate/i18n/dist/locale/en.json";
import nl from "@vee-validate/i18n/dist/locale/nl.json";
import fr from "@vee-validate/i18n/dist/locale/fr.json";
import it from "@vee-validate/i18n/dist/locale/it.json";
import es from "@vee-validate/i18n/dist/locale/es.json";
import hu from "@vee-validate/i18n/dist/locale/hu.json";
import pl from "@vee-validate/i18n/dist/locale/pl.json";
import ch from "./i18n/ch_vee_validate.json";
import pt from "./i18n/pt_vee_validate.json";
import sk from "./i18n/sk_vee_validate.json";

// **Mixins**
import languageMixin from "@/mixins/languageMixin";
import permissionMixin from "@/mixins/permissionMixin";

// **Filters**
import filters from "@/helpers/filters";

// **Styles**
import "@vuepic/vue-datepicker/src/VueDatePicker/style/main.scss";

// **VeeValidate Configuration**
configure({
  generateMessage: localize({
    ar,
    az,
    bg,
    bn,
    ca,
    cs,
    cy,
    de,
    nl,
    ch,
    en,
    fr,
    it,
    es,
    pt,
    hu,
    pl,
    sk,
  }),
});

if (AllRules !== undefined) {
  Object.keys(AllRules).forEach((rule) => {
    defineRule(rule, AllRules[rule]);
  });
}

// **Reusable SetupApp Function**
function setupApp(component, i18n, availableLocales) {
  const app = createApp(component)
    .use(VueAxios, axios)
    .use(router)
    .use(store)
    .use(i18n)
    .use(Vue3Toastify, {
      autoClose: 3000,
      position: toast.POSITION.TOP_RIGHT,
      transition: toast.TRANSITIONS.SLIDE,
    })
    .mixin(permissionMixin) // Register the mixin globally
    .mixin(languageMixin); // Register the mixin globally

  app.config.globalProperties.$filters = filters;
  // Provide availableLocales globally for language switching
  app.provide("availableLocales", availableLocales);

  startVersionPolling({ intervalMs: 60000 });
  app.mount("#app");

  const appLoading = document.getElementById("app-loading");
  if (appLoading) {
    appLoading.classList.add("app-loading--hide");
    window.setTimeout(() => {
      appLoading.remove();
    }, 220);
  }
}

// **Languages**
import enGB from "@/locales/en-GB";
import enUK from "@/locales/en-UK";
import enIE from "@/locales/en-IE";
import frFR from "@/locales/fr-FR";
import frCH from "@/locales/fr-CH";
import ptPT from "@/locales/pt-PT";
import esES from "@/locales/es-ES";
import frBE from "@/locales/fr-BE";
import nlBE from "@/locales/nl-BE";
import nlNL from "@/locales/nl-NL";
import fiFI from "@/locales/fi-FI";
import seSE from "@/locales/sv-SE";
import dkDK from "@/locales/da-DK";
import deAT from "@/locales/de-AT";
import deDE from "@/locales/de-DE";
import deCH from "@/locales/de-CH";
import itIT from "@/locales/it-IT";
import itCH from "@/locales/it-CH";
import roRO from "@/locales/ro-RO";
import ltLT from "@/locales/lt-LT";
import lvLV from "@/locales/lv-LV";
import huHU from "@/locales/hu-HU";
import plPL from "@/locales/pl-PL";
import csCZ from "@/locales/cs-CZ";
import skSK from "@/locales/sk-SK";
import bgBG from "@/locales/bg-BG";
import etEE from "@/locales/et-EE";
// new countres
import slSI from "@/locales/sl-SI";
import hrHR from "@/locales/hr-HR";
import srRS from "@/locales/sr-RS";
import bsBA from "@/locales/bs-BA";
import hrBA from "@/locales/hr-BA";
import srBA from "@/locales/sr-BA";
import nbNO from "@/locales/nb-NO";
import nnNO from "@/locales/nn-NO";
import noNO from "@/locales/no-NO";

// **Dynamic I18n Initialization**
// **Dynamic I18n Initialization**
function initializeI18n(locale, availableLocales) {
  // Check localStorage for a stored locale
  const storedLocale = localStorage.getItem("currentLocale");

  // Validate the stored locale against the available locales
  const activeLocale =
    storedLocale && availableLocales.includes(storedLocale)
      ? storedLocale
      : locale; // Use stored locale if valid, otherwise fallback to the default

  // Update localStorage with the active locale if it's different
  if (storedLocale !== activeLocale) {
    localStorage.setItem("currentLocale", activeLocale);
  }

  // Keep document language aligned with the active locale for SEO and accessibility.
  document.documentElement.setAttribute("lang", activeLocale);

  store.commit("SET_CURRENT_LOCALE", activeLocale);
  console.log("Active Locale for i18n:", activeLocale);

  const validationLocale = activeLocale.split("-");
  setLocale(validationLocale[0]);

  return createI18n({
    legacy: false,
    globalInjection: true,
    locale: activeLocale, // Use the validated or default locale
    // fallbackLocale: 'en-GB', // Optional: fallback to English if translations are missing
    messages: {
      "en-GB": enGB.messages,
      "en-UK": enUK.messages,
      "en-IE": enIE.messages,
      "fr-FR": frFR.messages,
      "fr-CH": frCH.messages,
      "pt-PT": ptPT.messages,
      "es-ES": esES.messages,
      "fr-BE": frBE.messages,
      "nl-BE": nlBE.messages,
      "nl-NL": nlNL.messages,
      "da-DK": dkDK.messages,
      "fi-FI": fiFI.messages,
      "de-DE": deDE.messages,
      "de-CH": deCH.messages,
      "de-AT": deAT.messages,
      "sv-SE": seSE.messages,
      "it-IT": itIT.messages,
      "it-CH": itCH.messages,
      "ro-RO": roRO.messages,
      "lv-LV": lvLV.messages,
      "lt-LT": ltLT.messages,
      "hu-HU": huHU.messages,
      "pl-PL": plPL.messages,
      "cs-CZ": csCZ.messages,
      "sk-SK": skSK.messages,
      "bg-BG": bgBG.messages,
      "et-EE": etEE.messages,
      "sl-SI": slSI.messages,
      "hr-HR": hrHR.messages,
      "sr-RS": srRS.messages,
      "bs-BA": bsBA.messages,
      "hr-BA": hrBA.messages,
      "sr-BA": srBA.messages,
      "nb-NO": nbNO.messages,
      "nn-NO": nnNO.messages,
      "no-NO": noNO.messages,
      // Add additional locales as needed
    },
  });
}

// **Tenant Detection, Maintenance Check and App Initialization**
applyPublicSeoTags();
router.afterEach(() => {
  applyPublicSeoTags();
});

detectCountryAndLoadConfig()
  .then(({ countryCode, baseURL, defaultLocale, availableLocales }) => {
    console.log("Tenant:", countryCode);
    console.log("API URL:", baseURL);
    console.log("Default Locale:", defaultLocale);
    console.log("Available Locales:", availableLocales);

    const storedLocale = localStorage.getItem("currentLocale");
    const initialLocale =
      storedLocale && availableLocales.includes(storedLocale)
        ? storedLocale
        : defaultLocale;

    if (storedLocale !== initialLocale) {
      localStorage.setItem("currentLocale", initialLocale);
    }

    // Initialize API Service
    ApiService.init(baseURL, countryCode, initialLocale, availableLocales);

    // Initialize i18n dynamically with validation
    const i18n = initializeI18n(initialLocale, availableLocales);

    return { i18n, availableLocales };
  })
  .then(({ i18n, availableLocales }) => {
    // Perform Maintenance Check
    return MaintenanceService.maintenance().then((response) => ({
      response,
      i18n,
      availableLocales,
    }));
  })
  .then(({ response, i18n, availableLocales }) => {
    const isUnderMaintenance = Object.keys(response.data).length > 0;
    const currentPath = window.location.pathname;

    if (isUnderMaintenance && currentPath !== "/maintenance") {
      window.location.href = "/maintenance";
    } else if (!isUnderMaintenance && currentPath === "/maintenance") {
      window.location.href = "/";
    } else {
      // Check if the user is logged in
      const loggedIn = Boolean(
        TokenService.getToken() || localStorage.getItem("token"),
      );

      const urlParams = new URLSearchParams(window.location.search);
      const hasB2CToken = Boolean(urlParams.get("token") && urlParams.get("userId"));

      if (loggedIn && !hasB2CToken) {
        localStorage.removeItem("tenantPermissions");
        localStorage.removeItem("tenantModuleConfigs");
        // Fetch permissions if the user is logged in, then setup and mount the app
        store
          .dispatch("fetchTenantPermissions", true)
          .then(() => {
            setupApp(App, i18n, availableLocales); // Proceed to mount the app even if permissions fail to load
          })
          .catch((error) => {
            console.error("Failed to fetch permissions:", error);
            // Optionally handle errors here, for example, redirect to login
            setupApp(App, i18n, availableLocales); // Proceed to mount the app even if permissions fail to load
          });
      } else {
        // If the user is not logged in, setup and mount the app directly
        setupApp(App, i18n, availableLocales);
      }
    }
  })
  .catch((error) => {
    console.error("Error during app initialization:", error);
    setupApp(App);
  });
